/**
 * 権限管理の管理系 API (`/features` `/permission-groups` `/users`) クライアントの共通処理 (権限管理 SBI-8)。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - 失敗時は `ApiError` (HTTP ステータス + サーバの `message`) を投げる。
 *   画面側 (`permissionAdminMessages.ts`) が、ステータスとメッセージから日本語の案内に変換する。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type { ApiErrorBody } from './types';

export interface AdminClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

/** JSON を送受信する 1 リクエスト。`body` が未指定ならボディなし。 */
export async function adminRequest<T>(
  options: AdminClientOptions,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = joinUrl(options.config.apiUrl, path);
  const idToken = await options.getIdToken();
  const fetchImpl = options.fetchImpl ?? fetch;

  const res = await fetchImpl(url, {
    method,
    headers: {
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
      Authorization: `Bearer ${idToken}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let errorBody: ApiErrorBody | undefined;
    try {
      errorBody = (await res.json()) as ApiErrorBody;
    } catch {
      // body が JSON で無い場合は無視
    }
    const message = errorBody?.message ?? 'API リクエストに失敗しました';
    throw new ApiError(res.status, message, errorBody);
  }

  return (await res.json()) as T;
}

/** パスの 1 区間として安全に埋め込む (UUID 想定だが、念のためエンコードする)。 */
export function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}
